import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import { isNullOrUndef } from "chart.js/helpers";
import { Pagination } from "@material-ui/lab";
import { titleize } from "inflected";
import { formatBooleanTrueFalse } from "../../../utils";
import parse from "html-react-parser";
import Button from "@material-ui/core/Button";
import { isWidthDown, Typography, withWidth } from "@material-ui/core";

const PopupWrap = styled.div`
  height: ${({ height }) => height};
  overflow-y: scroll;
  width: ${({ width }) => width};
`;

const PopupTable = styled.table`
  border-radius: 5px;
  border-collapse: collapse;
  border: 1px solid #ccc;
  width: 100%;
`;

const PopupRow = styled.tr`
  border-radius: 5px;
  &:nth-child(even) {
    background-color: #eee;
  }
`;

const PopupCell = styled.td`
  padding: 3px 6px;
  margin: 0;
`;

const PopupUl = styled.ul`
  list-style-type: none;
  margin: 0 !important;
  padding: 3px 0;
`;

const TruncatedTypographyTitle = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Popup = ({
  features,
  layers,
  height = "200px",
  maxWidth = "380px",
  size = "medium",
  setDataVizVisible,
  setDataVizWellNumber,
  setDataVizGraphType,
  width,
}) => {
  function getUniqueFeatures(array, comparatorProperty1, comparatorProperty2) {
    const existingFeatureKeys = {};
    // Because features come from tiled vector data, feature geometries may be split
    // or duplicated across tile boundaries and, as a result, features may appear
    // multiple times in query results.
    //concat two ids to make a unique id
    return array.filter((el) => {
      if (
        existingFeatureKeys[el[comparatorProperty1] + el[comparatorProperty2]]
      ) {
        return false;
      } else {
        existingFeatureKeys[
          el[comparatorProperty1] + el[comparatorProperty2]
        ] = true;
        return true;
      }
    });
  }

  const uniqueFeatures = getUniqueFeatures(features, "id", "id");

  const [page, setPage] = useState(1);
  const [feature, setFeature] = useState(uniqueFeatures?.[0]);
  const [excludeFields, setExcludeFields] = useState([]);

  const handlePageChange = (e, p) => {
    setPage(p);
  };

  useEffect(() => {
    if (uniqueFeatures?.length) {
      setFeature(uniqueFeatures[page - 1]);
    }
  }, [uniqueFeatures, page]);

  useEffect(() => {
    const excludedFields = layers?.find(
      (layer) => layer?.id === feature?.layer?.id
    )?.lreProperties?.popup?.excludeFields;
    setExcludeFields(excludedFields || []);
  }, [feature, layers]);

  let popupData;
  if (feature?.layer?.id === "riverside-locations-circle") {
    popupData = [
      ...(feature?.properties.has_flow_graph
        ? [
            [
              "Daily Flow",
              <>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "85px",
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setDataVizVisible(true);
                      setDataVizWellNumber(feature.properties.location_ndx);
                      setDataVizGraphType("has_flow_graph");
                    }}
                  >
                    View Data
                  </Button>
                </span>
              </>,
            ],
          ]
        : []),
      ...(feature?.properties.has_stage_graph
        ? [
            [
              "Stage Data",
              <>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "85px",
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setDataVizVisible(true);
                      setDataVizWellNumber(feature.properties.location_ndx);
                      setDataVizGraphType("has_stage_graph");
                    }}
                  >
                    View Data
                  </Button>
                </span>
              </>,
            ],
          ]
        : []),
      ...(feature?.properties.has_waterlevel_graph
        ? [
            [
              "Water Levels",
              <>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "85px",
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setDataVizVisible(true);
                      setDataVizWellNumber(feature.properties.location_ndx);
                      setDataVizGraphType("has_waterlevel_graph");
                    }}
                  >
                    View Data
                  </Button>
                </span>
              </>,
            ],
          ]
        : []),
      ...(feature?.properties.locations_urls
        ? [["More Info", feature?.properties.locations_urls]]
        : []),
      [
        "Period of Record",
        `${new Date(feature?.properties.por_start).getFullYear()} - ${new Date(
          feature?.properties.por_end
        ).getFullYear()}`,
      ],
      ["Record Count", feature?.properties.resultcount],
      ...(feature?.properties?.organizations
        ? [
            [
              "Organizations",
              feature?.properties.organizations
                .replace(/["{}[\]]/g, "")
                .replaceAll(",", ", "),
            ],
          ]
        : [["Organizations", "N/A"]]),
      ...(feature?.properties?.parameter_groups
        ? [
            [
              "Measurement Parameters",
              feature?.properties.parameter_groups
                .replace(/["{}[\]]/g, "")
                .replaceAll(",", ", "),
            ],
          ]
        : [["Parameter Groups", "N/A"]]),
      ["Location Type", feature?.properties.locationtype],
      ["Water Body", feature?.properties.waterbody_name],
      ["Latitude", feature?.properties.latitude],
      ["Longitude", feature?.properties.longitude],
    ];
  } else {
    popupData = excludeFields
      ? Object.entries(feature?.properties).reduce((acc, [key, value]) => {
          //MJB also removing entry if the value is an empty string, null, or undefined
          if (
            !excludeFields.includes(key) &&
            value !== "" &&
            !isNullOrUndef(value)
          ) {
            acc.push([key, value]);
          }
          return acc;
        }, [])
      : Object.entries(feature?.properties);
  }

  const [titleField, setTitleField] = useState("");

  useEffect(() => {
    const title = layers?.find((layer) => layer?.id === feature?.layer?.id)
      ?.lreProperties?.popup?.titleField;
    setTitleField(
      (title &&
        feature?.properties[title] &&
        `${feature?.properties[title]} (${titleize(
          feature?.layer?.source
        )})`) ||
        titleize(feature?.layer?.source)
    );
  }, [feature, layers]);

  if (!popupData) return null;
  return (
    <>
      <TruncatedTypographyTitle
        variant={isWidthDown("sm", width) ? "h4" : "h3"}
        style={{
          width: isWidthDown("sm", width) ? "205px" : maxWidth,
          marginTop: isWidthDown("sm", width) && "0",
          marginBottom: 0,
        }}
      >
        {feature?.layer?.id === "riverside-locations-circle"
          ? feature?.properties.location_name
          : titleField}
      </TruncatedTypographyTitle>
      <h3
        style={{
          marginTop: 0,
          width: width,
          marginBottom: isWidthDown("sm", width) && "2px",
        }}
      >
        {feature?.layer?.id === "riverside-locations-circle" &&
          feature?.properties.location_id}
      </h3>
      <PopupWrap
        height={isWidthDown("xs", width) ? "120px" : height}
        width={isWidthDown("sm", width) ? "205px" : maxWidth}
      >
        <PopupTable>
          <tbody>
            {popupData?.map(([key, value]) => {
              return (
                <PopupRow key={key}>
                  <PopupCell>
                    <strong>{titleize(key)}</strong>
                  </PopupCell>
                  <PopupCell>
                    {key === "More Info" ? (
                      value.split(",").map((item) => (
                        <div key={item}>
                          <a
                            target="_blank"
                            href={item.split(";")[1]}
                            rel="noreferrer"
                          >
                            {item.split(";")[0]}
                          </a>
                        </div>
                      ))
                    ) : typeof value === "string" && value.startsWith("<a ") ? (
                      <PopupUl>
                        {value.split(",").map((item) => (
                          <li key={item}>{parse(item)}</li>
                        ))}
                      </PopupUl>
                    ) : (
                      formatBooleanTrueFalse(value)
                    )}
                  </PopupCell>
                </PopupRow>
              );
            })}
          </tbody>
        </PopupTable>
      </PopupWrap>
      <Pagination
        style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}
        count={uniqueFeatures.length}
        size={isWidthDown("sm", width) ? "small" : size}
        siblingCount={isWidthDown("sm", width) ? 0 : 1}
        page={page}
        variant="outlined"
        shape="rounded"
        color="primary"
        onChange={handlePageChange}
      />
    </>
  );
};

export default withWidth()(Popup);
